import type {
  CreateWebsiteDestinationPageRequest,
  WebsiteDestinationPageDetail,
} from '@utazzo/shared';
import type { DefaultValues } from 'react-hook-form';

/** Parent text fields that are nullable on the wire but edited as '' in the form. */
const NULLABLE_TEXT_KEYS = [
  'heroHeading',
  'heroSubheading',
  'aboutHeading',
  'aboutHtml',
  'metaTitle',
  'metaDescription',
  'metaKeywords',
] as const;

export const CREATE_DEFAULTS: DefaultValues<CreateWebsiteDestinationPageRequest> = {
  slug: '',
  title: '',
  destinationId: null,
  categoryPageIds: [],
  heroHeading: '',
  heroSubheading: '',
  heroMediaId: null,
  heroVideoId: null,
  tileMediaId: null,
  aboutHeading: '',
  aboutHtml: '',
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  ogImageId: null,
  status: 'DRAFT',
  sortOrder: 0,
  sliders: [],
  faqs: [],
  reels: [],
  seoLinks: [],
  reviews: [],
};

/** The detail-page tabs, in display order. */
export type DetailTab =
  | 'details'
  | 'about'
  | 'sliders'
  | 'faq'
  | 'reels'
  | 'seoLinks'
  | 'reviews'
  | 'seo';

const TAB_ORDER: DetailTab[] = [
  'details',
  'about',
  'sliders',
  'faq',
  'reels',
  'seoLinks',
  'reviews',
  'seo',
];

export const TAB_LABEL: Record<DetailTab, string> = {
  details: 'Details',
  about: 'About',
  sliders: 'Sliders',
  faq: 'FAQ',
  reels: 'Reels',
  seoLinks: 'SEO links',
  reviews: 'Reviews',
  seo: 'SEO',
};

/** Which tab each top-level form field lives on, so a validation error can be revealed. */
const FIELD_TAB: Record<string, DetailTab> = {
  title: 'details',
  slug: 'details',
  destinationId: 'details',
  categoryPageIds: 'details',
  sortOrder: 'details',
  status: 'details',
  heroHeading: 'details',
  heroSubheading: 'details',
  heroMediaId: 'details',
  heroVideoId: 'details',
  tileMediaId: 'details',
  aboutHeading: 'about',
  aboutHtml: 'about',
  sliders: 'sliders',
  faqs: 'faq',
  reels: 'reels',
  seoLinks: 'seoLinks',
  reviews: 'reviews',
  metaTitle: 'seo',
  metaDescription: 'seo',
  metaKeywords: 'seo',
  ogImageId: 'seo',
};

/**
 * Given react-hook-form's error keys, return the earliest tab (in display
 * order) that contains an error, so the form can jump the user to the actual
 * problem instead of always landing on Details. Falls back to 'details'.
 */
export const firstErrorTab = (errorKeys: string[]): DetailTab => {
  const tabs = errorKeys.map((k) => FIELD_TAB[k]).filter((t): t is DetailTab => Boolean(t));
  return TAB_ORDER.find((t) => tabs.includes(t)) ?? 'details';
};

export const toForm = (d: WebsiteDestinationPageDetail): CreateWebsiteDestinationPageRequest => ({
  slug: d.slug,
  title: d.title,
  destinationId: d.destinationId,
  categoryPageIds: d.categoryPageIds,
  heroHeading: d.heroHeading ?? '',
  heroSubheading: d.heroSubheading ?? '',
  heroMediaId: d.heroMediaId,
  heroVideoId: d.heroVideoId,
  tileMediaId: d.tileMediaId,
  aboutHeading: d.aboutHeading ?? '',
  aboutHtml: d.aboutHtml ?? '',
  metaTitle: d.metaTitle ?? '',
  metaDescription: d.metaDescription ?? '',
  metaKeywords: d.metaKeywords ?? '',
  ogImageId: d.ogImageId,
  status: d.status === 'ARCHIVED' ? 'ARCHIVED' : d.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT',
  sortOrder: d.sortOrder,
  sliders: d.sliders.map((s) => ({
    marketingTagId: s.marketingTagId,
    heading: s.heading,
    subheading: s.subheading,
  })),
  faqs: d.faqs.map((f) => ({ question: f.question, answerHtml: f.answerHtml })),
  reels: d.reels.map((r) => ({
    url: r.url,
    posterMediaId: r.posterMediaId,
  })),
  seoLinks: d.seoLinks.map((l) => ({ groupLabel: l.groupLabel, label: l.label, href: l.href })),
  reviews: d.reviews.map((rv) => ({
    customerName: rv.customerName,
    summary: rv.summary,
    rating: rv.rating,
    portraitMediaId: rv.portraitMediaId,
    imageMediaIds: rv.imageMediaIds ?? [],
  })),
});

export const toPayload = (
  values: CreateWebsiteDestinationPageRequest,
): CreateWebsiteDestinationPageRequest => {
  const out: CreateWebsiteDestinationPageRequest = {
    ...values,
    reviews: values.reviews.map((rv) => ({
      ...rv,
      imageMediaIds: (rv.imageMediaIds ?? []).filter((id) => Boolean(id?.trim())),
    })),
  };
  for (const key of NULLABLE_TEXT_KEYS) {
    const v = out[key];
    if (typeof v === 'string' && v.trim() === '') out[key] = null;
  }
  return out;
};
